// @flow @jsx h

// IntersectionObserver is polyfilled:
/* eslint-disable compat/compat */

import { format } from 'date-fns'
import { Component, h } from 'preact'
import style from '../index.css'
import CharacterPresentation from './CharacterPresentation'
import Section from './Section'

import type { Props as BaseProps, Viewport } from '../types'

type Props = BaseProps & {
  muted: boolean,
  viewport: Viewport,
  onToggleMute: Function
}

type IntersectionEntry = {
  isIntersecting: boolean,
  intersectionRatio: number
}

type BodyIntersectionEntries = Array<IntersectionEntry> | null

type State = {
  sectionIntersectionEntries: IntersectionEntry[],
  bodyIntersectionEntries: BodyIntersectionEntries[]
}

class Content extends Component<Props, State> {
  intersectionObserver: IntersectionObserver
  sectionElms: Array<HTMLElement | null>
  sectionTriggerElms: Array<Array<HTMLElement | null> | null>

  constructor (props: Props) {
    super()

    this.sectionElms = props.data.body.map(section => null)

    this.sectionTriggerElms = props.data.body.map(section => {
      switch (section.type) {
        case 'section':
          return section.value.text.map(item => null)

        default:
          return null
      }
    })

    this.state = {
      sectionIntersectionEntries: props.data.body.map(section => ({
        intersectionRatio: -1,
        isIntersecting: false
      })),
      bodyIntersectionEntries: props.data.body.map(section => {
        switch (section.type) {
          case 'section':
            return section.value.text.map(item => ({
              intersectionRatio: -1,
              isIntersecting: false
            }))

          default:
            return null
        }
      })
    }
  }

  componentDidMount () {
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        // let { bodyIntersectionEntries } = this.state

        entries.forEach(entry => {
          let sectionIndex = -1
          let index = -1

          // Find indexes
          this.sectionTriggerElms.forEach((triggerElms, idx) => {
            if (triggerElms) {
              const triggerIndex = triggerElms.indexOf(entry.target)

              if (triggerIndex > -1) {
                sectionIndex = idx
                index = triggerIndex
              }
            }
          })

          // Update bodyIntersectionEntries
          if (index > -1) {
            const bodyIntersectionEntries = this.state.bodyIntersectionEntries.slice(
              0
            )

            // bodyIntersectionEntries = bodyIntersectionEntries.slice(0)
            if (bodyIntersectionEntries[sectionIndex]) {
              bodyIntersectionEntries[sectionIndex][index] = {
                isIntersecting: entry.isIntersecting,
                intersectionRatio: entry.intersectionRatio
              }
            }

            this.setState({ bodyIntersectionEntries })

            return
          }

          sectionIndex = this.sectionElms.indexOf(entry.target)

          // Update sectionIntersectionEntries
          if (sectionIndex > -1) {
            const sectionIntersectionEntries = this.state.sectionIntersectionEntries.slice(
              0
            )

            sectionIntersectionEntries[sectionIndex] = {
              isIntersecting: entry.isIntersecting,
              intersectionRatio: entry.intersectionRatio
            }

            this.setState({ sectionIntersectionEntries })
          }
        })
      },
      {
        rootMargin: '0px 0px 0px 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    )

    // Observe intersection of section elements
    this.sectionElms.forEach(sectionElm => {
      if (sectionElm) {
        this.intersectionObserver.observe(sectionElm)
      }
    })

    // Observe intersections of trigger elements
    this.sectionTriggerElms.forEach(triggerElms => {
      if (triggerElms) {
        triggerElms.forEach(triggerElm => {
          if (triggerElm) {
            this.intersectionObserver.observe(triggerElm)
          }
        })
      }
    })
  }

  handleRegisterSectionElement = (
    sectionIndex: number,
    sectionElm: HTMLElement
  ) => {
    this.sectionElms[sectionIndex] = sectionElm
  }

  handleRegisterTriggerElement = (
    sectionIndex: number,
    index: number,
    triggerElm: HTMLElement
  ) => {
    if (this.sectionTriggerElms[sectionIndex]) {
      this.sectionTriggerElms[sectionIndex][index] = triggerElm
    }
  }

  render () {
    const {
      data,
      mediaBaseUrl,
      muted,
      onAnalyticsEvent,
      onToggleMute,
      viewport
    } = this.props
    const { bodyIntersectionEntries, sectionIntersectionEntries } = this.state
    const publishedAt = new Date(data.publishedAt)

    return (
      <div className={style.article__content}>
        {data.body.map((item, idx) => {
          switch (item.type) {
            case 'characterPresentation':
              return (
                <CharacterPresentation
                  {...item}
                  {...sectionIntersectionEntries[idx]}
                  key={String(idx)}
                  index={idx}
                  mediaBaseUrl={mediaBaseUrl}
                  onAnalyticsEvent={onAnalyticsEvent}
                  onRegisterSectionElement={this.handleRegisterSectionElement}
                  viewport={viewport}
                />
              )
            case 'section':
              return (
                <Section
                  {...item}
                  {...sectionIntersectionEntries[idx]}
                  key={String(idx)}
                  index={idx}
                  mediaBaseUrl={mediaBaseUrl}
                  muted={muted}
                  intersectionEntries={bodyIntersectionEntries[idx]}
                  onAnalyticsEvent={onAnalyticsEvent}
                  onRegisterSectionElement={this.handleRegisterSectionElement}
                  onRegisterTriggerElement={this.handleRegisterTriggerElement}
                  onToggleMute={onToggleMute}
                  viewport={viewport}
                />
              )
            default:
              return (
                <div key={String(idx)}>{JSON.stringify(item, null, 2)}</div>
              )
          }
        })}
        <div className={style.article__publishedAt}>
          Publisert {format(publishedAt, 'DD.MM.YYYY')}, kl.{' '}
          {format(publishedAt, 'HH:mm')}
        </div>
      </div>
    )
  }
}

export default Content



// WEBPACK FOOTER //
// ./src/v1/components/Article/Content/index.js